// ------------------------------------------------------------------------------
// ArtWorksUploader
// author: mudas
// created: 2025/2/12
// ------------------------------------------------------------------------------

import React, { FC, PropsWithChildren, ReactElement, ReactNode } from 'react';
import { useControllableValue } from 'ahooks';

import { DraggerUploadFile, UploadFileItem } from 'common/types';
import UploadFileDragger, { UploadFileDraggerProps } from './UploadFileDragger';
import { MAX_IMAGE_SIZE, MAX_VIDEO_SIZE } from './types';

import EeveeIcon from 'common/components/EeveeIcon';
import { DeleteOutlined, ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { Button, Image, Tooltip, Typography } from '@fuxi/eevee-ui';
import PreviewImage from './PreviewImage';

import ExampleImageUploadWebp from 'common/assets/image/example-image-upload.webp';
import cx from './ArtWorksUploader.module.css';

export interface IProps extends Omit<UploadFileDraggerProps, 'value' | 'onChange'> {
  // 上传提示图标
  icon?: ReactNode;

  // 是否支持上传视频
  enableVideo?: boolean;

  // 列的数量（默认：4，每列中的图片会自动根据宽度 1:1 呈现）
  colums?: number;

  // 是否允许删除（默认：true）
  deletable?: boolean;

  value?: UploadFileItem[];
  onChange?: (value?: UploadFileItem[]) => void;
}

const ArtWorksUploader: FC<PropsWithChildren<IProps>> = (props, ref) => {
  const {
    icon,
    enableVideo = true,
    colums = 4,
    deletable = true,
    value,
    onChange,
    children,
    accept,
    listType = 'text',
    ...draggerProps
  } = props;

  const [artWorks, setArtWorks] = useControllableValue<UploadFileItem[]>(props, { defaultValue: [] });

  // --------------------------------------------------------------------------
  //
  // Event handlers
  //
  // --------------------------------------------------------------------------

  const handleChange = (file: DraggerUploadFile) => {
    const { uid, name, status, percent, response } = file;

    setArtWorks((works) => {
      // uid 与 url.fileId 皆可做唯一检测
      const target = works?.find((item) => item.uid === uid);

      if (target) {
        target.percent = percent ?? 0;
        target.status = status;

        if (status === 'done') {
          const { url, previewUrl, type, illegal } = response!;
          target.type = type;
          target.thumbUrl = previewUrl;
          target.url = url;
          target.illegal = !!illegal;
        }
      } else {
        works = works ?? [];
        works.push({ uid, name, percent: 0, status, illegal: false });
      }

      return [...works];
    });
  };

  const handleDelete = (uid: string) => {
    setArtWorks((works) => works?.filter((item) => item.uid !== uid));
  };

  const itemRender = (
    originNode: ReactElement,
    file: DraggerUploadFile,
    fileList: object[],
    { remove },
    // actions: { download: function; preview: function; remove: function },
  ) => {
    const { name, status, response } = file;

    return (
      <div className={cx('file-item')}>
        <div className="flex items-center gap-2">
          <EeveeIcon
            icon="upload-attachment"
            className={`color-icon-normal ${response?.illegal ? 'color-error!' : ''}`}
          />

          <Typography.Link ellipsis className={`flex-1 min-w-0 ${response?.illegal ? 'color-error!' : ''}`}>
            {name}
          </Typography.Link>

          {response?.illegal && (
            <Tooltip title="图片存在违禁内容，请重新上传">
              <ExclamationCircleOutlined className="color-error" />
            </Tooltip>
          )}
        </div>

        <Button
          type="text"
          size="small"
          className={cx('icon-delete', status)}
          icon={status === 'uploading' ? <LoadingOutlined /> : <DeleteOutlined />}
          onClick={remove}
          disabled={status === 'uploading'}
        />
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <UploadFileDragger
        className={cx('works-uploader-wrapper')}
        enableVideo={enableVideo}
        accept={accept ? accept : enableVideo ? 'image/*, video/mp4' : 'image/*'}
        listType={listType}
        showUploadList={listType === 'text'}
        onChange={handleChange}
        itemRender={listType === 'text' ? itemRender : undefined}
        namespace="user.artwork"
        {...draggerProps}>
        {children ?? (
          <div className="flex flex-col items-center font-size-4 line-height-loose">
            {icon ?? <Image src={ExampleImageUploadWebp} width={168} preview={false} />}

            <div className="mt-2">
              将图片/视频拖拽至此区域，也可以 <span className="color-primary">点击上传</span>
            </div>

            <Typography.Text type="secondary" className="font-size-base">
              支持图片格式 jpg、png、jpeg、gif，单张大小不超过 {draggerProps.maxImageSize || MAX_IMAGE_SIZE}MB
            </Typography.Text>

            {enableVideo && (
              <Typography.Text type="secondary" className="font-size-base">
                支持视频格式 mp4 ，单张大小不超过 {draggerProps.maxVideoSize || MAX_VIDEO_SIZE}MB
              </Typography.Text>
            )}
          </div>
        )}
      </UploadFileDragger>

      {listType === 'picture-card' && (
        <Image.PreviewGroup>
          <div key={artWorks?.length} className="grid gap-3" style={{ gridTemplateColumns: `repeat(${colums},1fr)` }}>
            {artWorks?.map((file) => (
              <PreviewImage
                key={file.uid}
                uid={file.uid}
                illegal={file.illegal}
                url={file.thumbUrl?.url!}
                percent={file.percent}
                status={file.status}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </Image.PreviewGroup>
      )}
    </div>
  );
};

export default ArtWorksUploader;
